/**
 * Trunnion AI - Workflow Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/config/queryKeys';
import { workflowsApi } from '@/api/workflows.api';
import { useNotificationStore } from '@/stores/notificationStore';
import type {
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  CloneWorkflowRequest,
} from '@/types';

export function useWorkflowList(filters: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: queryKeys.workflows.list(filters),
    queryFn: () => workflowsApi.list(filters),
  });
}

export function useWorkflow(id: string) {
  return useQuery({
    queryKey: queryKeys.workflows.detail(id),
    queryFn: () => workflowsApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateWorkflow() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: (workflow: CreateWorkflowRequest) => workflowsApi.create(workflow),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.all });
      addNotification({
        type: 'success',
        title: 'Workflow Created',
        message: 'Workflow has been created successfully.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Creation Failed',
        message: error.message,
      });
    },
  });
}

export function useUpdateWorkflow() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkflowRequest }) =>
      workflowsApi.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.detail(variables.id),
      });
      addNotification({
        type: 'success',
        title: 'Workflow Saved',
        message: 'Workflow has been saved successfully.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: error.message,
      });
    },
  });
}

export function useDeleteWorkflow() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: (id: string) => workflowsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.all });
      addNotification({
        type: 'success',
        title: 'Workflow Deleted',
        message: 'Workflow has been deleted.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: error.message,
      });
    },
  });
}

export function useExecuteWorkflow() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input?: Record<string, unknown>;
    }) => workflowsApi.execute(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.executions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      addNotification({
        type: 'success',
        title: 'Execution Started',
        message: `Execution ${data.executionId} has been started.`,
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Execution Failed',
        message: error.message,
      });
    },
  });
}

export function useCloneWorkflow() {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: CloneWorkflowRequest }) =>
      workflowsApi.clone(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.all });
      addNotification({
        type: 'success',
        title: 'Workflow Cloned',
        message: 'Workflow has been cloned successfully.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Clone Failed',
        message: error.message,
      });
    },
  });
}
